import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import { useStore } from '@stores/RootStore';
import { GlobeIcon } from '@icons/GlobeIcon';
import { GoogleColorIcon } from '@icons/GoogleColorIcon';
import { MicrosoftIcon } from '@icons/MicrosoftIcon';
import { OktaIcon } from '@icons/OktaIcon';
import sessionStorage from '@repositories/browserStorage/sessionStorage/SessionStorage';
import type { TenantSsoProviderPublicDTO } from '@repositories/tenantSsoProviderRepository/tenantSsoProvider.types';

const DISCOVERY_TIMEOUT_MS = 5000;

/**
 * issuerUri 기반 SSO 프로바이더 아이콘 매핑
 */
export const getSsoProviderIcon = (issuerUri: string, size = 20): ReactNode => {
  const uri = issuerUri.toLowerCase();
  if (uri.includes('google') || uri.includes('accounts.google.com'))
    return <GoogleColorIcon width={size} height={size} />;
  if (uri.includes('microsoft') || uri.includes('login.microsoftonline'))
    return <MicrosoftIcon width={size} height={size} />;
  if (uri.includes('okta')) return <OktaIcon width={size} height={size} />;
  return <GlobeIcon width={size} height={size} />;
};

/**
 * SSO 프로바이더 OIDC 리다이렉트 로직을 캡슐화한 훅
 * - OIDC Discovery (.well-known/openid-configuration) 조회
 * - sessionStorage에 oauth_provider, sso_registration_id, sso_tenant_id 저장
 * - authorization_endpoint로 리다이렉트
 */
export const useSsoRedirect = () => {
  const { t } = useTranslation();
  const {
    uiStore: { alertStore },
  } = useStore();

  const [redirectingProviderId, setRedirectingProviderId] = useState<string | null>(null);
  const isRedirecting = !!redirectingProviderId;

  const showError = (message: string) => {
    alertStore.open({
      title: t('Sign_In_01'),
      message,
      useCancel: false,
    });
  };

  const redirectToSsoProvider = async (provider: TenantSsoProviderPublicDTO, loginHint?: string) => {
    const providerId = `${provider.tenantId}:${provider.registrationId}`;
    setRedirectingProviderId(providerId);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DISCOVERY_TIMEOUT_MS);

    try {
      // OIDC Discovery로 authorization_endpoint 조회
      const issuerUri = provider.issuerUri.replace(/\/$/, '');
      const discoveryUrl = `${issuerUri}/.well-known/openid-configuration`;
      const discoveryResponse = await fetch(discoveryUrl, { signal: controller.signal });

      if (!discoveryResponse.ok) {
        showError(t('SsoProvider.Login.DiscoveryError'));
        setRedirectingProviderId(null);
        return;
      }

      const discoveryDoc = await discoveryResponse.json();
      const authorizationEndpoint = discoveryDoc.authorization_endpoint;

      if (!authorizationEndpoint) {
        showError(t('SsoProvider.Login.DiscoveryError'));
        setRedirectingProviderId(null);
        return;
      }

      // SSO 프로바이더 정보를 sessionStorage에 저장 (콜백 처리 시 사용)
      sessionStorage.set('oauth_provider', 'sso');
      sessionStorage.set('sso_registration_id', provider.registrationId);
      sessionStorage.set('sso_tenant_id', provider.tenantId);
      // 리다이렉트 후 select-tenant 복귀 시 discover API용 이메일 복원
      if (loginHint) {
        sessionStorage.set('sso_login_email', loginHint);
      }

      const redirectUri = provider.redirectUri || `${window.location.protocol}//${window.location.host}/login`;
      const scopes = provider.scopes || 'openid profile email';

      // CSRF 방지를 위한 state 값 생성 및 저장
      const state = crypto.randomUUID();
      sessionStorage.set('oauth_state', state);

      const queryString = qs.stringify({
        response_type: 'code',
        client_id: provider.clientId,
        redirect_uri: redirectUri,
        scope: scopes.replace(/,/g, ' '),
        state,
        prompt: 'login',
        ...(loginHint && { login_hint: loginHint }),
      });

      window.location.href = `${authorizationEndpoint}?${queryString}`;
    } catch {
      showError(t('SsoProvider.Login.DiscoveryError'));
      setRedirectingProviderId(null);
    } finally {
      clearTimeout(timeoutId);
    }
  };

  return { redirectToSsoProvider, isRedirecting, redirectingProviderId };
};
